import { ChangeDetectorRef, Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { DateUtility } from '../../../classes/dateUtility';
import { FormValidation } from '../../../classes/formValidation';
import { eNotificationTypes } from '../../../interfaces/constants';
import { ISelectOptionBoolean, ISelectOptions } from '../../../interfaces/abstract';
import { eCommodityTypeCalculators, ICommodityType, YieldUnitId } from '../../../models/commodity-type/interfaces';
import { CommodityService } from '../../../models/commodity-type/service';
import { CropType } from '../../../models/crop-type/cropType';
import { ICropTypeListItem, ICropTypeViewModel } from '../../../models/crop-type/interfaces';
import { CropTypeService } from '../../../models/crop-type/service';
import { FertilizationEventService } from '../../fertilization-event/service';
import { IPublicNitrogenRecommendation, IPublicNitrogenRecommendationModel,
	IPublicNitrogenUptakePoint } from '../../fertilization-event/interfaces';
import { NotificationService } from '../../../services/notification.service';
import { IAgmMapClick } from '../../shared/interfaces';
import { navigationViews, UpdateService } from '../../../services/update.service';
import { TranslateService } from '../../localization/service';
import { CalculatorSettingsForm } from '../../planting-settings/calculatorSettingsForm';
import { PlantingAdvancedEdit } from '../../planting-settings/plantingAdvancedEdit';
import { Planting } from '../../planting-settings/planting';
import { CompostComponent } from '../../planting-settings/sections/nitrogen-calculator/compost';
import { CoverCropComponent } from '../../planting-settings/sections/nitrogen-calculator/cover-crop';
import { ManureComponent } from '../../planting-settings/sections/nitrogen-calculator/manure';
import * as Highcharts from 'highcharts';

interface ISpringLeafField {
	name: 'LeafN' | 'LeafK' | 'LeafCa' | 'LeafMg' | 'LeafB',
	label: string,
	unit: '%' | 'ppm'
}

interface ISampleDepth {
	value: number,
	label: string
}

interface IDateRange {
	minDate: Date,
	maxDate: Date
}

interface IUptakeAxis {
	xMin: number,
	xMax: number,
	yMin: number,
	yMax: number
}

interface IDestroyable {
	destroy: () => void
}

interface IAxisWithExtremes extends Highcharts.AxisObject {
	setExtremes: (min: number, max: number, redraw?: boolean) => void,
	min: number,
	max: number
}

interface IChartWithResetZoom extends Highcharts.ChartObject {
	resetZoomButton: IDestroyable
}

interface IChartSelection {
	resetSelection?: boolean
}

export type NCalculatorFields = 'CommodityTypeId' | 'CropTypeId' | 'WetDate' | 'HarvestDate'
	| 'EventDate' | 'DaysToNextFertilization' | 'FertilizationsPerMonth'
	| 'SoilN' | 'SampleDepth' | 'Latitude' | 'Longitude' | 'Company'
	| 'AgeOfCrop' | 'IsHighVigor' | 'YieldTarget'
	| 'LeafN' | 'LeafK' | 'LeafCa' | 'LeafMg' | 'LeafB'
	| 'ManureN' | 'CompostN' | 'CoverCropN';

@Component({
	selector: 'public-n-calculator',
	templateUrl: 'n-calculator.component.html',
	styleUrls: ['n-calculator.component.scss']
})
export class NitrogenRecommendationCalculatorComponent implements OnInit, OnDestroy {

	@ViewChild('uptakeChart', { static: false }) private _uptakeChart: ElementRef;

	public commodityTypes: ICommodityType[];
	public cropTypes: ICropTypeListItem[];
	public form: FormGroup;
	public calculatorForm: FormGroup;
	public isSaving = false;
	public isFertilizationsPerMonth = false;
	public isTree = false;
	public showSpringLeaves = false;
	public yieldUnitId: YieldUnitId = null;
	public YIELD_UNIT_IDS = YieldUnitId;
	public vigorOptions: ISelectOptionBoolean[] = [
		{ key: false, value: 'Low' },
		{ key: true, value: 'High' }
	];
	public ageOptions: ISelectOptions[];
	public springLeafFields: ISpringLeafField[] = [
		{ name: 'LeafN', label: 'N', unit: '%' },
		{ name: 'LeafK', label: 'K', unit: '%' },
		{ name: 'LeafCa', label: 'Ca', unit: '%' },
		{ name: 'LeafMg', label: 'Mg', unit: '%' },
		{ name: 'LeafB', label: 'B', unit: 'ppm' }
	];
	public detail: IPublicNitrogenRecommendation = null;
	public formChanged = false;
	public uptakeZoomedOut = false;
	public sampleDepths: ISampleDepth[] = [
		{ value: 1, label: '0 to 1 ft' },
		{ value: 2, label: '1 to 2 ft' }
	];

	public wetDateRange: IDateRange;
	public harvestDateRange: IDateRange;
	public eventDateRange: IDateRange;

	public get f(): { [key in NCalculatorFields]: AbstractControl } {
		return this.form.controls as { [key in NCalculatorFields]: AbstractControl };
	}

	private _cropTypeViewModel: ICropTypeViewModel;
	private _subscriptions$: Subject<boolean>;
	private _uptakeBracket: IDestroyable;
	private _uptakeChartObject: Highcharts.ChartObject;
	private _intervalAxis: IUptakeAxis;
	private _calculatedValue: string;
	private readonly MAX_DATE_RANGE = 365;

	constructor(
		private _cdr: ChangeDetectorRef,
		private _commodityService: CommodityService,
		private _cropTypeService: CropTypeService,
		private _dialog: MatDialog,
		private _fb: FormBuilder,
		private _fertilizationEventService: FertilizationEventService,
		private _notificationService: NotificationService,
		private _translateService: TranslateService,
		private _updateService: UpdateService,
	) {
		this.wetDateRange = { minDate: null, maxDate: null };
		this.harvestDateRange = { minDate: null, maxDate: null };
		this.eventDateRange = { minDate: null, maxDate: null };
		this.ageOptions = [];
	}

	ngOnInit(): void {
		this._subscriptions$ = new Subject();
		this._initializeForm();
		this.calculatorForm = new CalculatorSettingsForm(this._fb, this._subscriptions$)
			.initialize(PlantingAdvancedEdit.CalculatorSettingsDefault);
		this._updateService.setCurrentPage(navigationViews.N_CALCULATOR);
		this._commodityService.list().then((response) => {
			this.commodityTypes = response.filter(x => x.CommodityTypeCalculator !== eCommodityTypeCalculators.alfalfa);
		});
	}

	ngOnDestroy(): void {
		if (!this._subscriptions$) {
			return;
		}

		this._subscriptions$.next(true);
		this._subscriptions$.complete();
	}

	public clickMap(event: IAgmMapClick): void {
		this.f.Latitude.setValue(event.coords.lat);
		this.f.Longitude.setValue(event.coords.lng);
	}

	public calculate(): void {
		if (!this.form.valid || this.f.Company.value) {
			if (!this.form.valid) {
				this._notificationService.generateNotifcation({
					type: eNotificationTypes.ERROR,
					message: 'Please fill in all required fields before calculating'
				});
			}

			return;
		}

		this.isSaving = true;
		this._fertilizationEventService.publicNitrogenRecommendation(this._recommendationRequest()).then((detail: IPublicNitrogenRecommendation) => {
			let point: IPublicNitrogenUptakePoint;

			detail.EventDate = DateUtility.DotNetToDate(detail.EventDate as unknown as string);
			detail.NextFertilizationDate = DateUtility.DotNetToDate(detail.NextFertilizationDate as unknown as string);

			for (point of detail.Uptake) {
				point.Date = DateUtility.DotNetToDate(point.Date as unknown as string);
			}

			this.detail = detail;
			this.formChanged = false;
			this._calculatedValue = JSON.stringify(this.form.getRawValue());
			this.isSaving = false;
			this._cdr.detectChanges();
			this._drawUptakeChart();
		}).catch(() => {
			this.isSaving = false;
		});
	}

	public zoomUptake(fullSeason: boolean): void {
		this._applyUptakeExtremes(this._uptakeChartObject, fullSeason);
		this.uptakeZoomedOut = fullSeason;
	}

	private _drawUptakeChart(): void {
		let eventDate: number;
		let nextDate: number;
		let points: [number, number][];
		let axis: IUptakeAxis;
		let uptakeAtEvent: number;
		let uptakeAtNext: number;
		let difference: number;
		let component: NitrogenRecommendationCalculatorComponent;
		let drawBracket: (chart: Highcharts.ChartObject) => void;

		if (!this.detail || !this.detail.Uptake || this.detail.Uptake.length === 0 || !this._uptakeChart) {
			return;
		}

		eventDate = DateUtility.DateToUTC(this.detail.EventDate);
		nextDate = DateUtility.DateToUTC(this.detail.NextFertilizationDate);
		points = this.detail.Uptake.map((point): [number, number] => {
			return [DateUtility.DateToUTC(point.Date), point.Amount];
		});
		axis = this._chartWindow(points, eventDate, nextDate);
		this._intervalAxis = axis;
		this.uptakeZoomedOut = false;
		uptakeAtEvent = this.detail.NUptake;
		uptakeAtNext = this.detail.NUptakeRequired;
		difference = this.detail.NUptakeDifference;
		this._uptakeBracket = null;
		component = this;
		drawBracket = (chart: Highcharts.ChartObject) => {
			this._drawIntervalBracket(chart, nextDate, uptakeAtEvent, uptakeAtNext, difference);
		};

		this._uptakeChartObject = Highcharts.chart(this._uptakeChart.nativeElement, {
			chart: {
				height: 280,
				spacingTop: 28,
				spacingRight: 108,
				zoomType: 'x',
				events: {
					load: function (this: Highcharts.ChartObject) {
						component._applyUptakeExtremes(this, false);
					},
					redraw: function (this: Highcharts.ChartObject) {
						drawBracket(this);
					},
					selection: (function (this: Highcharts.ChartObject, event: IChartSelection): boolean {
						let resetZoomButton: IDestroyable;

						if (!event.resetSelection) {
							return true;
						}

						component._applyUptakeExtremes(this, false);
						component.uptakeZoomedOut = false;
						component._cdr.detectChanges();
						resetZoomButton = (this as IChartWithResetZoom).resetZoomButton;

						if (resetZoomButton) {
							resetZoomButton.destroy();
						}

						return false;
					} as any)
				}
			},
			title: {
				text: null
			},
			legend: {
				enabled: false
			},
			tooltip: {
				valueDecimals: 2,
				valueSuffix: ' lbs N/acre'
			},
			xAxis: {
				type: 'datetime',
				minPadding: 0,
				maxPadding: 0,
				plotBands: [{
					from: eventDate,
					to: nextDate,
					color: 'rgba(46, 125, 50, 0.18)'
				}],
				plotLines: [{
					value: eventDate,
					color: '#2e7d32',
					width: 2,
					label: {
						text: 'Event',
						style: {
							color: '#2e7d32'
						}
					}
				}, {
					value: nextDate,
					color: '#2e7d32',
					width: 2,
					dashStyle: 'ShortDash',
					label: {
						text: 'Next fertilization',
						style: {
							color: '#2e7d32'
						}
					}
				}]
			},
			yAxis: {
				title: {
					text: 'lbs N/acre'
				},
				startOnTick: false,
				endOnTick: false
			},
			series: [{
				name: 'Crop N uptake',
				type: 'line',
				data: points
			}]
		});
	}

	private _drawIntervalBracket(chart: Highcharts.ChartObject, nextDate: number,
		uptakeAtEvent: number, uptakeAtNext: number, difference: number): void {

		let xAxis: IAxisWithExtremes;
		let xNext: number;
		let yEvent: number;
		let yNext: number;
		let bracketX: number;
		let labelY: number;
		let group: IDestroyable;
		let renderer: any;

		if (this._uptakeBracket) {
			this._uptakeBracket.destroy();
			this._uptakeBracket = null;
		}

		xAxis = chart.xAxis[0] as IAxisWithExtremes;

		if (xAxis.min && xAxis.max &&
			(nextDate < xAxis.min || nextDate > xAxis.max)) {
			return;
		}

		renderer = (chart as any).renderer;
		xNext = chart.xAxis[0].toPixels(nextDate, false);
		yEvent = chart.yAxis[0].toPixels(uptakeAtEvent, false);
		yNext = chart.yAxis[0].toPixels(uptakeAtNext, false);
		bracketX = xNext + 10;
		labelY = (yEvent + yNext) / 2 + 4;

		group = renderer.g().attr({ zIndex: 7 }).add();
		renderer.path([
			'M', bracketX - 8, yEvent,
			'L', bracketX, yEvent,
			'L', bracketX, yNext,
			'L', bracketX - 8, yNext
		]).attr({
			stroke: '#333333',
			'stroke-width': 1.5,
			fill: 'none'
		}).add(group);
		renderer.text(difference.toFixed(2) + ' lbs N/acre', bracketX + 6, labelY)
			.css({
				fontSize: '12px',
				color: '#333333'
			})
			.add(group);

		this._uptakeBracket = group;
	}

	private _applyUptakeExtremes(chart: Highcharts.ChartObject, fullSeason: boolean): void {
		let xAxis: IAxisWithExtremes;
		let yAxis: IAxisWithExtremes;

		if (!chart || !this._intervalAxis) {
			return;
		}

		xAxis = chart.xAxis[0] as IAxisWithExtremes;
		yAxis = chart.yAxis[0] as IAxisWithExtremes;

		if (fullSeason) {
			xAxis.setExtremes(null, null, false);
			yAxis.setExtremes(null, null, false);
		} else {
			xAxis.setExtremes(this._intervalAxis.xMin, this._intervalAxis.xMax, false);
			yAxis.setExtremes(this._intervalAxis.yMin, this._intervalAxis.yMax, false);
		}

		chart.redraw();
	}

	/** Window around the event date and the next fertilization, with a little season on either side. */
	private _chartWindow(points: [number, number][], eventDate: number, nextDate: number): IUptakeAxis {

		let span: number;
		let pad: number;
		let xMin: number;
		let xMax: number;
		let low: number;
		let high: number;
		let yPad: number;
		let i: number;

		span = Math.max(nextDate - eventDate, 24 * 60 * 60 * 1000);
		pad = Math.max(span * 0.5, 3 * 24 * 60 * 60 * 1000);
		xMin = Math.max(points[0][0], eventDate - pad);
		xMax = Math.min(points[points.length - 1][0], nextDate + pad);
		low = points[0][1];
		high = points[0][1];

		for (i = 0; i < points.length; i++) {
			if (points[i][0] < xMin || points[i][0] > xMax) {
				continue;
			}

			if (points[i][1] < low) {
				low = points[i][1];
			}

			if (points[i][1] > high) {
				high = points[i][1];
			}
		}

		yPad = Math.max((high - low) * 0.2, 1);

		return {
			xMin: xMin,
			xMax: xMax,
			yMin: low < 0 ? this._round(low - yPad) : Math.max(0, this._round(low - yPad)),
			yMax: this._round(high + yPad)
		};
	}

	private _recommendationRequest(): IPublicNitrogenRecommendationModel {
		return {
			CropTypeId: this.f.CropTypeId.value,
			WetDate: this.f.WetDate.value,
			HarvestDate: this.f.HarvestDate.value,
			EventDate: this.f.EventDate.value,
			DaysToNextFertilization: this.isFertilizationsPerMonth ? null : this.f.DaysToNextFertilization.value,
			FertilizationsPerMonth: this.isFertilizationsPerMonth ? this.f.FertilizationsPerMonth.value : null,
			AgeOfCrop: this.f.AgeOfCrop.value,
			IsHighVigor: this.f.IsHighVigor.value,
			YieldTarget: this.f.YieldTarget.value,
			LeafN: this.f.LeafN.value,
			LeafK: this.f.LeafK.value,
			LeafCa: this.f.LeafCa.value,
			LeafMg: this.f.LeafMg.value,
			LeafB: this.f.LeafB.value,
			ManureN: this.f.ManureN.value || 0,
			CompostN: this.f.CompostN.value || 0,
			CoverCropN: this.f.CoverCropN.value || 0,
			SoilN: this.f.SoilN.value,
			SampleDepth: this.f.SampleDepth.value,
			Latitude: this.f.Latitude.value,
			Longitude: this.f.Longitude.value
		};
	}

	private _round(value: number): number {
		return Math.round(value * 100) / 100;
	}

	private _configureOrchard(commodity: ICommodityType): void {
		this.isTree = CropType.isTreeByInterface(commodity.CommodityTypeCalculator);
		this.showSpringLeaves = CropType.areSpringLeafNutrientsRequired(commodity.CommodityTypeCalculator);
		this.yieldUnitId = commodity.YieldUnitId;

		if (this.isTree) {
			this.f.AgeOfCrop.enable();
			this.f.IsHighVigor.enable();
			this.f.YieldTarget.enable();
			this.f.ManureN.enable();
			this.f.CompostN.enable();
			this.f.CoverCropN.enable();
			this.f.AgeOfCrop.setValidators([Validators.required]);
			this.f.YieldTarget.setValidators([
				Validators.required,
				FormValidation.greaterThanValidator(0)
			]);
			this.f.ManureN.setValidators([Validators.min(0)]);
			this.f.CompostN.setValidators([Validators.min(0)]);
			this.f.CoverCropN.setValidators([Validators.min(0)]);
		} else {
			this._disableOrchardField('AgeOfCrop');
			this._disableOrchardField('IsHighVigor');
			this._disableOrchardField('YieldTarget');
			this._disableOrchardField('ManureN');
			this._disableOrchardField('CompostN');
			this._disableOrchardField('CoverCropN');
			this.f.IsHighVigor.setValue(true);
			this.f.ManureN.setValue(0);
			this.f.CompostN.setValue(0);
			this.f.CoverCropN.setValue(0);
		}

		this._setLeafValidators();
		this.f.AgeOfCrop.updateValueAndValidity();
		this.f.YieldTarget.updateValueAndValidity();
	}

	private _setLeafValidators(): void {
		let leaf: ISpringLeafField;

		for (leaf of this.springLeafFields) {
			if (this.isTree && this.showSpringLeaves) {
				this.f[leaf.name].enable();
				this.f[leaf.name].clearValidators();
			} else {
				this._disableOrchardField(leaf.name);
			}

			this.f[leaf.name].updateValueAndValidity({ emitEvent: false });
		}
	}

	public openManureCalculator(event: MouseEvent): void {
		event.preventDefault();
		this._openCreditCalculator(ManureComponent, 'ManureN');
	}

	public openCompostCalculator(event: MouseEvent): void {
		event.preventDefault();
		this._openCreditCalculator(CompostComponent, 'CompostN');
	}

	public openCoverCropCalculator(event: MouseEvent): void {
		event.preventDefault();
		this._openCreditCalculator(CoverCropComponent, 'CoverCrop');
	}

	private _openCreditCalculator(
		component: typeof ManureComponent | typeof CompostComponent | typeof CoverCropComponent,
		groupName: 'ManureN' | 'CompostN' | 'CoverCrop'
	): void {
		let dialogConfig: MatDialogConfig;
		let controlName: NCalculatorFields;

		controlName = groupName === 'CoverCrop' ? 'CoverCropN' : groupName;
		dialogConfig = {
			data: {
				form: this.calculatorForm.get(groupName),
				control: this.f[controlName]
			},
			width: '600px',
			disableClose: true
		};

		this._dialog.open(component as any, dialogConfig);
	}

	private _disableOrchardField(name: NCalculatorFields): void {
		this.f[name].reset();
		this.f[name].clearValidators();
		this.f[name].disable();
	}

	private _initializeForm(): void {
		this.form = this._fb.group({
			CommodityTypeId: [null, [Validators.required, FormValidation.greaterThanValidator(0)]],
			CropTypeId: [null, [Validators.required, FormValidation.greaterThanValidator(0)]],
			WetDate: [null, [Validators.required]],
			HarvestDate: [null],
			EventDate: [null, [Validators.required]],
			DaysToNextFertilization: [{ value: null, disabled: true }],
			FertilizationsPerMonth: [{ value: null, disabled: true }],
			SoilN: [null, [Validators.min(0)]],
			SampleDepth: [1],
			Latitude: [null, [Validators.required]],
			Longitude: [null, [Validators.required]],
			Company: [''],
			AgeOfCrop: [{ value: null, disabled: true }],
			IsHighVigor: [{ value: true, disabled: true }],
			YieldTarget: [{ value: null, disabled: true }],
			LeafN: [{ value: null, disabled: true }],
			LeafK: [{ value: null, disabled: true }],
			LeafCa: [{ value: null, disabled: true }],
			LeafMg: [{ value: null, disabled: true }],
			LeafB: [{ value: null, disabled: true }],
			ManureN: [{ value: 0, disabled: true }],
			CompostN: [{ value: 0, disabled: true }],
			CoverCropN: [{ value: 0, disabled: true }]
		});

		this.f.HarvestDate.setValidators([
			Validators.required,
			FormValidation.isDate,
			FormValidation.mustBeAfterDate(this.f.WetDate),
			FormValidation.mustBeWithinDateRange(this.f.WetDate, this.MAX_DATE_RANGE)
		]);

		this.f.EventDate.setValidators([
			Validators.required,
			FormValidation.isDate,
			FormValidation.minDateValidatorWithControl(this.f.WetDate, true),
			FormValidation.maxDateValidatorWithControl(this.f.HarvestDate, true)
		]);

		this.form.valueChanges.pipe(takeUntil(this._subscriptions$)).subscribe(() => {
			if (!this._calculatedValue) {
				return;
			}

			this.formChanged = JSON.stringify(this.form.getRawValue()) !== this._calculatedValue;
		});

		this.f.CommodityTypeId.valueChanges.pipe(takeUntil(this._subscriptions$)).subscribe(() => {
			let item: ICommodityType;

			if (!this.commodityTypes || !this.f.CommodityTypeId.value) {
				return;
			}

			item = this.commodityTypes.find(x => x.Id === this.f.CommodityTypeId.value);

			if (!item) {
				return;
			}

			this.isFertilizationsPerMonth = this._usesFertilizationsPerMonth(item.CommodityTypeCalculator);
			this._setFrequencyValidators();
			this._configureOrchard(item);
			this.f.CropTypeId.reset();
			this._cropTypeViewModel = null;
			this.cropTypes = [];
			this.ageOptions = [];
			this._cropTypeService.getCropTypesAdmin(false, this.f.CommodityTypeId.value).then((response) => {
				this.cropTypes = response;
			});
		});

		this.f.CropTypeId.valueChanges.pipe(takeUntil(this._subscriptions$)).subscribe(() => {
			let cropType: ICropTypeListItem;

			if (!this.f.CropTypeId.value || !this.cropTypes) {
				return;
			}

			this._cropTypeService.getCropModel(this.f.CropTypeId.value).then((res) => {
				this._cropTypeViewModel = res;
				this._applyDefaultHarvestDate();
			});

			cropType = this.cropTypes.find(x => x.Id === this.f.CropTypeId.value);

			if (this.isTree && cropType) {
				this.ageOptions = PlantingAdvancedEdit.generateAgeOfCropDropdown(cropType.MaxAge, this._translateService);
			} else {
				this.ageOptions = [];
			}
		});

		this.f.WetDate.valueChanges.pipe(takeUntil(this._subscriptions$)).subscribe(() => {
			if (!this.f.WetDate.value) {
				this.harvestDateRange.minDate = null;
				this.harvestDateRange.maxDate = null;
				this.eventDateRange.minDate = null;
			} else {
				this.harvestDateRange.minDate = DateUtility.addDays(this.f.WetDate.value, 1);
				this.harvestDateRange.maxDate = DateUtility.addDays(this.f.WetDate.value, this.MAX_DATE_RANGE);
				this.eventDateRange.minDate = this.f.WetDate.value;
			}

			this.f.HarvestDate.updateValueAndValidity({ emitEvent: false });
			this.f.EventDate.updateValueAndValidity({ emitEvent: false });
			this._applyDefaultHarvestDate();
		});

		this.f.HarvestDate.valueChanges.pipe(takeUntil(this._subscriptions$)).subscribe(() => {
			if (!this.f.HarvestDate.value) {
				this.wetDateRange.minDate = null;
				this.wetDateRange.maxDate = null;
				this.eventDateRange.maxDate = null;
			} else {
				this.wetDateRange.minDate = DateUtility.addDays(this.f.HarvestDate.value, -this.MAX_DATE_RANGE);
				this.wetDateRange.maxDate = DateUtility.addDays(this.f.HarvestDate.value, -1);
				this.eventDateRange.maxDate = this.f.HarvestDate.value;
			}

			this.f.EventDate.updateValueAndValidity({ emitEvent: false });
		});
	}

	private _usesFertilizationsPerMonth(calculator: string): boolean {
		if (calculator === eCommodityTypeCalculators.strawberry ||
			calculator === eCommodityTypeCalculators.caneberry) {

			return true;
		}

		return false;
	}

	private _setFrequencyValidators(): void {
		if (this.isFertilizationsPerMonth) {
			this.f.DaysToNextFertilization.reset();
			this.f.DaysToNextFertilization.disable();
			this.f.FertilizationsPerMonth.enable();
			this.f.FertilizationsPerMonth.setValidators([
				Validators.required,
				Validators.min(1),
				Validators.max(10),
				FormValidation.integerValidator()
			]);
		} else {
			this.f.FertilizationsPerMonth.reset();
			this.f.FertilizationsPerMonth.disable();
			this.f.DaysToNextFertilization.enable();
			this.f.DaysToNextFertilization.setValidators([
				Validators.required,
				Validators.min(1),
				FormValidation.integerValidator()
			]);
		}

		this.f.DaysToNextFertilization.updateValueAndValidity();
		this.f.FertilizationsPerMonth.updateValueAndValidity();
	}

	private _applyDefaultHarvestDate(): void {
		if (!this.f.WetDate.value || this.f.HarvestDate.value || !this._cropTypeViewModel) {
			return;
		}

		this.f.HarvestDate.setValue(Planting.setHarvestDateFromDaysToEndDate(
			this._cropTypeViewModel.DaysToEndDate,
			this.f.WetDate.value,
			this.isTree
		));
	}
}
